import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";

const links = [
  { label: "Services", href: "/services", testid: "nav-link-services" },
  { label: "Results", href: "/#results", testid: "nav-link-results" },
  { label: "Case Studies", href: "/case-studies", testid: "nav-link-case-studies" },
  { label: "Process", href: "/process", testid: "nav-link-process" },
  { label: "About", href: "/about", testid: "nav-link-about" },
  { label: "Contact", href: "/contact", testid: "nav-link-contact" },
];

const go = (e, href) => {
  if (href.startsWith("/#")) {
    e.preventDefault();
    if (window.location.pathname === "/") {
      document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = href;
    }
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_8px_30px_-15px_rgba(15,23,42,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="nav-logo-mark" className="shrink-0">
            <Wordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={l.testid}
                onClick={(e) => go(e, l.href)}
                className="link-underline text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact"
              data-testid="nav-cta-contact-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0FA89E] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0C8E85]"
            >
              Book a consultation
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 overflow-hidden"
          >
            <div className="px-4 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={(e) => {
                    go(e, l.href);
                    setOpen(false);
                  }}
                  className="text-base font-medium text-slate-700"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-contact-button"
                onClick={() => setOpen(false)}
                className="inline-flex w-fit items-center gap-2 rounded-full bg-[#0FA89E] px-5 py-2.5 text-sm font-semibold text-white"
              >
                Book a consultation
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
